import React, { useState, useRef } from 'react';
import { Upload, X, Camera, AlertCircle, CheckCircle, Loader2, MapPin, Clock, Bug } from 'lucide-react';
import type { Grade } from '../types';
import { extractPhotoMetadata, compressImage, PhotoMetadata } from '../utils/photoMetadata';
import { DebugExifModal } from './DebugExifModal';

interface PhotoUploadModeProps {
  isOpen: boolean;
  onClose: () => void;
  onProcessPhoto: (
    blob: Blob,
    latitude: number,
    longitude: number,
    timestamp: string
  ) => Promise<{ success: boolean; grade?: Grade }>;
  audioEnabled: boolean;
}

interface PhotoStatus {
  file: File;
  status: 'pending' | 'processing' | 'success' | 'error';
  grade?: Grade;
  error?: string;
  hasLocation: boolean;
  hasTimestamp: boolean;
  metadata?: PhotoMetadata;
}

export function PhotoUploadMode({
  isOpen,
  onClose,
  onProcessPhoto,
  audioEnabled
}: PhotoUploadModeProps) {
  const [photos, setPhotos] = useState<PhotoStatus[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [debugMode, setDebugMode] = useState(false);
  const [debugModalOpen, setDebugModalOpen] = useState(false);
  const [selectedMetadata, setSelectedMetadata] = useState<PhotoMetadata | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const newPhotos: PhotoStatus[] = Array.from(files).map((file) => ({
      file,
      status: 'pending',
      hasLocation: false,
      hasTimestamp: false
    }));

    setPhotos(newPhotos);
  };

  const processPhotos = async () => {
    if (photos.length === 0) return;

    setIsProcessing(true);
    setCurrentPhotoIndex(0);

    for (let i = 0; i < photos.length; i++) {
      setCurrentPhotoIndex(i);
      const photo = photos[i];

      setPhotos((prev) =>
        prev.map((p, idx) => (idx === i ? { ...p, status: 'processing' } : p))
      );

      try {
        const metadata = await extractPhotoMetadata(photo.file);

        setPhotos((prev) =>
          prev.map((p, idx) => (idx === i ? { ...p, metadata } : p))
        );

        if (!metadata.hasLocation) {
          const debugMsg = metadata.debugInfo?.gpsFieldsFound.length
            ? `GPS fields found but incomplete: [${metadata.debugInfo.gpsFieldsFound.join(', ')}]. Check console for details.`
            : 'No GPS location data found in photo. Ensure location services were enabled when photo was taken.';

          setPhotos((prev) =>
            prev.map((p, idx) =>
              idx === i
                ? {
                    ...p,
                    status: 'error',
                    error: debugMsg,
                    hasLocation: false,
                    hasTimestamp: metadata.hasTimestamp
                  }
                : p
            )
          );
          continue;
        }

        if (!metadata.hasTimestamp) {
          setPhotos((prev) =>
            prev.map((p, idx) =>
              idx === i
                ? {
                    ...p,
                    status: 'error',
                    error: 'No timestamp found in photo',
                    hasLocation: metadata.hasLocation,
                    hasTimestamp: false
                  }
                : p
            )
          );
          continue;
        }

        const compressedBlob = await compressImage(photo.file);

        const result = await onProcessPhoto(
          compressedBlob,
          metadata.latitude!,
          metadata.longitude!,
          metadata.timestamp!
        );

        if (result.success && result.grade) {
          setPhotos((prev) =>
            prev.map((p, idx) =>
              idx === i
                ? {
                    ...p,
                    status: 'success',
                    grade: result.grade,
                    hasLocation: true,
                    hasTimestamp: true
                  }
                : p
            )
          );
        } else {
          setPhotos((prev) =>
            prev.map((p, idx) =>
              idx === i
                ? {
                    ...p,
                    status: 'error',
                    error: 'Failed to analyze photo',
                    hasLocation: metadata.hasLocation,
                    hasTimestamp: metadata.hasTimestamp
                  }
                : p
            )
          );
        }
      } catch (error) {
        console.error('Error processing photo:', error);
        setPhotos((prev) =>
          prev.map((p, idx) =>
            idx === i
              ? { ...p, status: 'error', error: error instanceof Error ? error.message : 'Unknown error' }
              : p
          )
        );
      }

      await new Promise((resolve) => setTimeout(resolve, 500));
    }

    setIsProcessing(false);

    if (audioEnabled && 'speechSynthesis' in window) {
      const successCount = photos.filter((p) => p.status === 'success').length;
      const utterance = new SpeechSynthesisUtterance(
        `Batch processing complete! ${successCount} of ${photos.length} photos processed successfully.`
      );
      utterance.lang = 'en-GB';
      window.speechSynthesis.speak(utterance);
    }
  };

  const handleClose = () => {
    setPhotos([]);
    setIsProcessing(false);
    setCurrentPhotoIndex(0);
    setDebugModalOpen(false);
    setSelectedMetadata(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  if (!isOpen) return null;

  const successCount = photos.filter((p) => p.status === 'success').length;
  const errorCount = photos.filter((p) => p.status === 'error').length;
  const pendingCount = photos.filter((p) => p.status === 'pending').length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <Upload className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-bold text-gray-800">Upload Photos</h2>
          </div>
          <button
            onClick={handleClose}
            disabled={isProcessing}
            className="text-gray-500 hover:text-gray-700 transition-colors disabled:opacity-50"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-gray-600 text-sm">
            Upload photos with GPS location and timestamp metadata. Each photo will be analyzed by AI
            and logged at its original location and time.
          </p>

          {photos.length === 0 && (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <Camera className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 mb-4">Select photos to upload</p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileSelect}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
              >
                Choose Photos
              </button>
            </div>
          )}

          {photos.length > 0 && (
            <>
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <h3 className="font-semibold text-gray-700">Processing Summary</h3>
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div className="bg-white p-3 rounded text-center">
                    <div className="text-2xl font-bold text-gray-800">{photos.length}</div>
                    <div className="text-gray-500">Total</div>
                  </div>
                  <div className="bg-green-50 p-3 rounded text-center">
                    <div className="text-2xl font-bold text-green-700">{successCount}</div>
                    <div className="text-gray-500">Success</div>
                  </div>
                  <div className="bg-red-50 p-3 rounded text-center">
                    <div className="text-2xl font-bold text-red-700">{errorCount}</div>
                    <div className="text-gray-500">Failed</div>
                  </div>
                </div>

                {isProcessing && (
                  <div className="bg-blue-50 p-3 rounded">
                    <div className="flex items-center gap-2 mb-2">
                      <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                      <span className="text-sm font-medium text-blue-700">
                        Processing photo {currentPhotoIndex + 1} of {photos.length}
                      </span>
                    </div>
                    <div className="w-full bg-blue-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{
                          width: `${((currentPhotoIndex + 1) / photos.length) * 100}%`
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-2 max-h-64 overflow-y-auto">
                {photos.map((photo, index) => (
                  <div
                    key={index}
                    className={`p-3 rounded-lg border ${
                      photo.status === 'success'
                        ? 'bg-green-50 border-green-200'
                        : photo.status === 'error'
                        ? 'bg-red-50 border-red-200'
                        : photo.status === 'processing'
                        ? 'bg-blue-50 border-blue-200'
                        : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">
                          {photo.file.name}
                        </p>
                        <div className="flex items-center gap-3 mt-1">
                          {photo.hasLocation && (
                            <div className="flex items-center gap-1 text-xs text-green-600">
                              <MapPin className="w-3 h-3" />
                              <span>GPS</span>
                            </div>
                          )}
                          {photo.hasTimestamp && (
                            <div className="flex items-center gap-1 text-xs text-green-600">
                              <Clock className="w-3 h-3" />
                              <span>Time</span>
                            </div>
                          )}
                        </div>
                        {photo.grade && (
                          <p className="text-xs text-gray-600 mt-1">Grade: {photo.grade}</p>
                        )}
                        {photo.error && (
                          <p className="text-xs text-red-600 mt-1">{photo.error}</p>
                        )}
                        {debugMode && photo.metadata && (
                          <button
                            onClick={() => {
                              setSelectedMetadata(photo.metadata!);
                              setDebugModalOpen(true);
                            }}
                            className="text-xs text-blue-600 hover:text-blue-700 mt-1 flex items-center gap-1"
                          >
                            <Bug className="w-3 h-3" />
                            View Debug Info
                          </button>
                        )}
                      </div>
                      <div className="flex-shrink-0">
                        {photo.status === 'success' && (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        )}
                        {photo.status === 'error' && (
                          <AlertCircle className="w-5 h-5 text-red-600" />
                        )}
                        {photo.status === 'processing' && (
                          <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex gap-3">
                {!isProcessing && pendingCount > 0 && (
                  <button
                    onClick={processPhotos}
                    className="flex-1 bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-4 rounded-lg transition-colors"
                  >
                    Process {photos.length} Photo{photos.length !== 1 ? 's' : ''}
                  </button>
                )}
                {!isProcessing && (
                  <button
                    onClick={() => {
                      setPhotos([]);
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }}
                    className="flex-1 bg-gray-500 hover:bg-gray-600 text-white font-semibold py-3 px-4 rounded-lg transition-colors"
                  >
                    Clear
                  </button>
                )}
                {isProcessing && (
                  <button
                    disabled
                    className="flex-1 bg-blue-500 opacity-50 text-white font-semibold py-3 px-4 rounded-lg cursor-not-allowed"
                  >
                    Processing...
                  </button>
                )}
              </div>

              {!isProcessing && (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
                >
                  Add More Photos
                </button>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileSelect}
                className="hidden"
              />
            </>
          )}

          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>Audio feedback: {audioEnabled ? 'Enabled' : 'Disabled'}</span>
            <button
              onClick={() => setDebugMode(!debugMode)}
              className={`flex items-center gap-1 px-3 py-1 rounded transition-colors ${
                debugMode
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Bug className="w-4 h-4" />
              Debug Mode {debugMode ? 'ON' : 'OFF'}
            </button>
          </div>

          <DebugExifModal
            isOpen={debugModalOpen}
            onClose={() => setDebugModalOpen(false)}
            metadata={selectedMetadata}
          />
        </div>
      </div>
    </div>
  );
}
